'use client';

import * as React from 'react';
import { CalendarIcon, XIcon } from 'lucide-react';

import { cn } from '../lib/utils';
import { Button } from '../shadcn/button';
import { Calendar } from '../shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../shadcn/select';
import { Trans } from './trans';

/**
 * `DateField` — the canonical date picker for the whole app.
 *
 * Replaces every native `<input type="date">` / `type="month">`. The native
 * control was banned after BAL-381: its browser-rendered "Today" button
 * silently cleared the user's selection, and `type="date"` yields an
 * inconsistent UI per browser/OS. This is the popover calendar (with
 * month/year dropdowns and a "Done" button) that the dancer-wizard proved out,
 * extracted here so there is ONE date UI (CLAUDE.md Rule 8 / Rule 12).
 *
 * Timezone-safe by construction (Critical Rule 19): the wire value is a plain
 * `YYYY-MM-DD` (or `YYYY-MM` for month granularity) string. We never call
 * `new Date('YYYY-MM-DD')` or `.toISOString()` — both shift the day across UTC.
 * The two helpers below are the `@kit/ui`-local twins of `parseLocalDate` /
 * `formatLocalDate` in `apps/web/lib/date-utils` (which this package cannot
 * import).
 *
 * react-hook-form: no wrapper needed — `value`/`onChange` already match a
 * `Controller`'s `field`:
 * ```tsx
 * <Controller
 *   name="dateOfBirth"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <DateField
 *       value={field.value ?? ''}
 *       onChange={field.onChange}
 *       name={field.name}
 *       hasError={!!fieldState.error}
 *       max={todayIso}
 *     />
 *   )}
 * />
 * ```
 */

/** Build a local `YYYY-MM-DD` from a Date without crossing UTC. */
function toIsoDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse a local date-only string to a Date at local midnight (Rule 19 safe). */
function parseIsoDateLocal(value: string): Date | undefined {
  if (!value) return undefined;
  // Accept 'YYYY-MM' (month granularity) by snapping to day 01.
  const normalized = /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return undefined;
  const parsed = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export interface DateFieldProps {
  /** Selected value: `YYYY-MM-DD` (day) or `YYYY-MM` (month), or '' when empty. */
  value: string;
  /** Emits the new value; '' when cleared. */
  onChange: (value: string) => void;
  id?: string;
  /**
   * When set, renders a hidden `<input name={name}>` mirroring `value` so DOM
   * `input[name=…]` selectors (tests, autofill) still resolve.
   */
  name?: string;
  /** Trigger label when `value` is empty. Defaults to `common:selectDate`. */
  placeholder?: React.ReactNode;
  disabled?: boolean;
  /** Applies the destructive border to the trigger. */
  hasError?: boolean;
  /** Inclusive lower bound, `YYYY-MM-DD` (or `YYYY-MM`). */
  min?: string;
  /** Inclusive upper bound, `YYYY-MM-DD` (or `YYYY-MM`). e.g. DoB → today. */
  max?: string;
  /** Calendar header: month/year dropdowns ('dropdown', default) or a label. */
  captionLayout?: 'label' | 'dropdown';
  /** react-day-picker v9 year-dropdown lower bound. Derived from `min` if unset. */
  startMonth?: Date;
  /** react-day-picker v9 year-dropdown upper bound. Derived from `max` if unset. */
  endMonth?: Date;
  /** Where an empty calendar opens (e.g. DoB ≈ 20 years ago). */
  defaultMonth?: Date;
  /** Shows a Clear button that emits '' (for optional/nullable fields). */
  clearable?: boolean;
  /** 'day' (YYYY-MM-DD, default) or 'month' (YYYY-MM, month+year dropdowns). */
  granularity?: 'day' | 'month';
  align?: 'start' | 'center' | 'end';
  /** className for the trigger button. */
  className?: string;
  'aria-label'?: string;
}

export function DateField({
  value,
  onChange,
  id,
  name,
  placeholder,
  disabled,
  hasError,
  min,
  max,
  captionLayout = 'dropdown',
  startMonth,
  endMonth,
  defaultMonth,
  clearable,
  granularity = 'day',
  align = 'start',
  className,
  'aria-label': ariaLabel,
}: DateFieldProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseIsoDateLocal(value);

  const minDate = min ? parseIsoDateLocal(min) : undefined;
  const maxDate = max ? parseIsoDateLocal(max) : undefined;

  // Year-dropdown bounds: honour explicit props, else derive from min/max, else
  // a wide sensible span (120y back … max/today).
  const resolvedEndMonth = endMonth ?? maxDate ?? new Date();
  const resolvedStartMonth = React.useMemo(() => {
    if (startMonth) return startMonth;
    if (minDate) return minDate;
    const d = new Date(resolvedEndMonth);
    d.setFullYear(d.getFullYear() - 120);
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMonth, min, max]);

  const label = selected
    ? selected.toLocaleDateString(
        undefined,
        granularity === 'month'
          ? { year: 'numeric', month: 'long' }
          : { year: 'numeric', month: 'long', day: 'numeric' },
      )
    : null;

  // Month granularity renders inline (two selects), not behind a popover.
  // Every reference implementation of a month/year date range (Upwork,
  // Coursera, Peerlist, Behance, Braintrust) exposes the selects directly: a
  // popover + Done button adds two clicks and hides which half is still empty.
  if (granularity === 'month') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <MonthYearPicker
          id={id}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          disabled={disabled}
          hasError={hasError}
          aria-label={ariaLabel}
        />
        {clearable && value ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 shrink-0"
            disabled={disabled}
            onClick={() => onChange('')}
            aria-label="Clear"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        ) : null}
        {name ? <input type="hidden" name={name} value={value} /> : null}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'h-11 w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            hasError ? 'border-destructive focus:border-destructive' : '',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {label ?? placeholder ?? (
            <Trans i18nKey="common:selectDate" defaults="Select a date" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? defaultMonth}
          captionLayout={captionLayout}
          startMonth={resolvedStartMonth}
          endMonth={resolvedEndMonth}
          disabled={[
            ...(minDate ? [{ before: minDate }] : []),
            ...(maxDate ? [{ after: maxDate }] : []),
          ]}
          onSelect={(date) => {
            if (date) onChange(toIsoDateLocal(date));
          }}
          autoFocus
        />
        <div className="flex gap-2 border-t p-2">
          {clearable && value ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              <Trans i18nKey="common:clear" defaults="Clear" />
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            className="flex-1"
            onClick={() => setOpen(false)}
          >
            <Trans i18nKey="common:done" defaults="Done" />
          </Button>
        </div>
      </PopoverContent>
      {name ? <input type="hidden" name={name} value={value} /> : null}
    </Popover>
  );
}

/**
 * Month-granularity picker: month + year `Select`s emitting `YYYY-MM`. Kept
 * separate from the day calendar because react-day-picker has no month-only
 * selection mode.
 */
function MonthYearPicker({
  id,
  value,
  onChange,
  min,
  max,
  disabled,
  hasError,
  'aria-label': ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  hasError?: boolean;
  'aria-label'?: string;
}) {
  // Pending selection lives locally: the first pick (month OR year alone) is not
  // a valid `YYYY-MM`, so it cannot round-trip through `value`. Deriving both
  // selects from `value` deadlocked the picker — neither half ever stuck.
  const [pending, setPending] = React.useState(() => {
    const [y = '', m = ''] = value ? value.split('-') : [];
    return { year: y, month: m };
  });

  // Re-sync when the value changes from the outside (reset, edit-mode load,
  // Clear). Adjusting state during render is React's sanctioned alternative to
  // a setState-in-effect here — no cascading render, no stale first paint.
  const [lastValue, setLastValue] = React.useState(value);
  if (lastValue !== value) {
    setLastValue(value);
    const [y = '', m = ''] = value ? value.split('-') : [];
    setPending({ year: y, month: m });
  }

  const monthStr = pending.month;
  const yearStr = pending.year;

  const minYear = min
    ? Number(min.slice(0, 4))
    : new Date().getFullYear() - 100;
  const maxYear = max ? Number(max.slice(0, 4)) : new Date().getFullYear() + 5;

  const years = React.useMemo(() => {
    const out: number[] = [];
    for (let y = maxYear; y >= minYear; y--) out.push(y);
    return out;
  }, [minYear, maxYear]);

  const months = [
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
  ];

  const emit = (nextMonth: string, nextYear: string) => {
    if (!nextMonth || !nextYear) {
      setPending({ month: nextMonth, year: nextYear });
      return;
    }
    // Clamp: switching to a boundary year can push an already-picked month out
    // of range (end-date before start-date), so snap instead of emitting it.
    let next = `${nextYear}-${nextMonth}`;
    if (min && next < min) next = min;
    if (max && next > max) next = max;
    const [y = '', m = ''] = next.split('-');
    setPending({ year: y, month: m });
    onChange(next);
  };

  const monthLabel = (m: string) =>
    new Date(2000, Number(m) - 1, 1).toLocaleDateString(undefined, {
      month: 'long',
    });

  // A month is out of range only at the boundary years (e.g. end-date min is
  // the start date: same year → months before it are invalid).
  const monthDisabled = (m: string) => {
    if (!yearStr) return false;
    if (min && yearStr === min.slice(0, 4) && m < min.slice(5, 7)) return true;
    if (max && yearStr === max.slice(0, 4) && m > max.slice(5, 7)) return true;
    return false;
  };

  const triggerClass = cn(
    'h-11 w-full',
    hasError ? 'border-destructive focus:border-destructive' : '',
  );

  return (
    <div className="grid flex-1 grid-cols-2 gap-2">
      <Select
        value={monthStr}
        onValueChange={(m) => emit(m, yearStr)}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          className={triggerClass}
          aria-label={ariaLabel ?? 'Month'}
        >
          <SelectValue
            placeholder={<Trans i18nKey="common:month" defaults="Month" />}
          />
        </SelectTrigger>
        <SelectContent>
          {months.map((m) => (
            <SelectItem key={m} value={m} disabled={monthDisabled(m)}>
              {monthLabel(m)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={yearStr}
        onValueChange={(y) => emit(monthStr, y)}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClass} aria-label="Year">
          <SelectValue
            placeholder={<Trans i18nKey="common:year" defaults="Year" />}
          />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
